import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Pencil } from 'lucide-react';

// Mentions @[type:id|libellé] dans les focus, les redevabilités et les domaines (cercle, rôle, projet, règle).
export const mentionPattern = /@\[(circle|role|project|rule):(\d+)\|([^\]]+)\]/g;

export const MENTION_TYPE_LABELS = { circle: 'Cercle', role: 'Rôle', project: 'Projet', rule: 'Règle' };

export const mentionChipStyles = {
  circle: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  role: 'bg-amber-50 text-amber-700 border-amber-100',
  project: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  rule: 'bg-sky-50 text-sky-700 border-sky-200',
};

const mentionTextStyles = {
  circle: 'text-indigo-600 bg-indigo-50/70',
  role: 'text-amber-600 bg-amber-50/70',
  project: 'text-emerald-600 bg-emerald-50/70',
  rule: 'text-sky-700 bg-sky-50/80',
};

/**
 * Ce que la page Organisation partage avec ses champs à mentions, où qu'ils soient (fiche, gouvernance) : les éléments
 * citables (« @ ») et les règles, pour afficher une règle citée avec sa définition et l'ouvrir.
 */
export const OrgMentionsContext = React.createContext({ mentionItems: [], ruleById: new Map(), onOpenRule: null });

// Redevabilités et domaines citent cercles, rôles et règles (les projets, c'est le focus).
export const structureMentionItems = (items) =>
  (items || []).filter((item) => ['circle', 'role', 'rule'].includes(item.type));

export const parseMentions = (text) => {
  if (!text) return [];
  const parts = [];
  let lastIndex = 0;
  String(text).replace(mentionPattern, (match, type, id, label, offset) => {
    if (offset > lastIndex) parts.push({ type: 'text', value: text.slice(lastIndex, offset) });
    parts.push({ type: 'mention', mentionType: type, id: Number(id), label });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < text.length) parts.push({ type: 'text', value: text.slice(lastIndex) });
  return parts;
};

export const mentionedProjectIds = (text) =>
  parseMentions(text)
    .filter((part) => part.type === 'mention' && part.mentionType === 'project')
    .map((part) => part.id);

// Un focus garde ses anciens projets : seuls ceux en cours comptent et s'affichent d'abord.
export const isInProgress = (project) => String(project?.status || '').toLowerCase() === 'in_progress';

const PROJECT_STATUS = { completed: 'terminé', done: 'terminé', proposition: 'proposition' };
export const projectStatusLabel = (project) => PROJECT_STATUS[String(project?.status || '').toLowerCase()] || '';

/** « + 12 terminés », « + 3 propositions » ou « + 4 autres » : ce que cache le bouton. */
export const oldLabel = (projects) => {
  const n = projects.length;
  const s = n > 1 ? 's' : '';
  const labels = new Set(projects.map(projectStatusLabel));
  if (labels.size === 1 && labels.has('terminé')) return `+ ${n} terminé${s}`;
  if (labels.size === 1 && labels.has('proposition')) return `+ ${n} proposition${s}`;
  return `+ ${n} autre${s} (terminés, propositions)`;
};

/** Projets cités par un focus, ceux en cours d'un côté, les autres (terminés, propositions, inconnus) de l'autre. */
export const splitFocusProjects = (text, projectById) => {
  const ids = [...new Set(mentionedProjectIds(text))];
  return {
    current: ids.filter((id) => isInProgress(projectById?.get(id))),
    others: ids.filter((id) => !isInProgress(projectById?.get(id))),
  };
};

export const plainMentions = (text) =>
  String(text || '').replace(mentionPattern, (_m, _type, _id, label) => `@${label}`);

const buildSegments = (rawText) => {
  if (!rawText) return [];
  const segments = [];
  let lastIndex = 0;
  rawText.replace(mentionPattern, (match, type, id, label, offset) => {
    if (offset > lastIndex) {
      const text = rawText.slice(lastIndex, offset);
      segments.push({ type: 'text', raw: text, display: text });
    }
    segments.push({ type: 'mention', raw: match, display: `@${label}`, mentionType: type, id: Number(id), label });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < rawText.length) {
    const text = rawText.slice(lastIndex);
    segments.push({ type: 'text', raw: text, display: text });
  }
  return segments;
};

const toDisplay = (segments) => segments.map((seg) => seg.display).join('');
const toRaw = (segments) => segments.map((seg) => seg.raw).join('');

const splitAt = (segments, index) => {
  if (index <= 0) return { left: [], right: [...segments] };
  let remaining = index;
  const left = [];
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const length = segment.display.length;
    if (remaining > length) {
      left.push(segment);
      remaining -= length;
      continue;
    }
    if (remaining === length) {
      left.push(segment);
      return { left, right: segments.slice(i + 1) };
    }
    const before = segment.display.slice(0, remaining);
    const after = segment.display.slice(remaining);
    if (before) left.push({ type: 'text', raw: before, display: before });
    const right = after
      ? [{ type: 'text', raw: after, display: after }, ...segments.slice(i + 1)]
      : segments.slice(i + 1);
    return { left, right };
  }
  return { left, right: [] };
};

/**
 * Champ texte avec mentions : « @ » propose cercles, rôles, projets et règles. Valide sur Entrée ou en quittant le
 * champ. `multiline` : sans bordure, sur autant de lignes que le texte (une redevabilité longue se lit en entier) ;
 * Entrée n'y fait pas de saut de ligne.
 */
export const MentionInput = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled = false,
  mentionItems = [],
  placeholder = '',
  getMentionStyle,
  inputRef: externalRef,
  className = '',
  autoFocus = false,
  onDone,
  multiline = false,
  ariaLabel,
}) => {
  const [rawDraft, setRawDraft] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [activeQuery, setActiveQuery] = useState(null);
  const localRef = useRef(null);
  const inputRef = externalRef || localRef;
  const overlayRef = useRef(null);
  const ignoreBlurRef = useRef(false);

  useEffect(() => {
    setRawDraft(value || '');
  }, [value]);

  useLayoutEffect(() => {
    const el = multiline ? inputRef.current : null;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  });

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const segments = useMemo(() => buildSegments(rawDraft), [rawDraft]);
  const displayDraft = useMemo(() => toDisplay(segments), [segments]);

  const updateRaw = (nextRaw) => {
    setRawDraft(nextRaw);
    if (onChange) onChange(nextRaw);
  };

  const updateSuggestions = (text, caretPos) => {
    const match = text.slice(0, caretPos).match(/@([^\s,]*)$/);
    if (!match || (match[1] || '').startsWith('[')) {
      setSuggestions([]);
      setActiveQuery(null);
      return;
    }
    const query = (match[1] || '').toLowerCase();
    setActiveQuery({ start: caretPos - match[0].length, end: caretPos });
    setSuggestions(mentionItems.filter((item) => (item.name || '').toLowerCase().includes(query)).slice(0, 6));
  };

  const handleChange = (event) => {
    if (disabled) return;
    const nextDisplay = multiline ? event.target.value.replace(/\s*\n\s*/g, ' ') : event.target.value;
    const caretPos = event.target.selectionStart ?? nextDisplay.length;
    if (nextDisplay === displayDraft) return;
    let start = 0;
    while (start < displayDraft.length && start < nextDisplay.length && displayDraft[start] === nextDisplay[start]) {
      start += 1;
    }
    let endPrev = displayDraft.length - 1;
    let endNext = nextDisplay.length - 1;
    while (endPrev >= start && endNext >= start && displayDraft[endPrev] === nextDisplay[endNext]) {
      endPrev -= 1;
      endNext -= 1;
    }
    const inserted = nextDisplay.slice(start, endNext + 1);
    const { left, right: rest } = splitAt(segments, start);
    const { right } = splitAt(rest, Math.max(0, endPrev - start + 1));
    const next = [...left];
    if (inserted) next.push({ type: 'text', raw: inserted, display: inserted });
    next.push(...right);
    updateRaw(toRaw(next));
    updateSuggestions(nextDisplay, caretPos);
  };

  const applyMention = (item) => {
    if (!activeQuery) return;
    const safeLabel = (item.name || '').replace(/\|/g, '').replace(/\]/g, '');
    const token = `@[${item.type}:${item.id}|${safeLabel}]`;
    const displayToken = `@${safeLabel}`;
    const { left, right: rest } = splitAt(segments, activeQuery.start);
    const { right } = splitAt(rest, activeQuery.end - activeQuery.start);
    updateRaw(toRaw([...left, { type: 'mention', raw: token, display: displayToken }, ...right]));
    setSuggestions([]);
    setActiveQuery(null);
    requestAnimationFrame(() => {
      const pos = toDisplay(left).length + displayToken.length;
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const commit = () => {
    if (onCommit && rawDraft !== (value || '')) onCommit(rawDraft);
  };

  // En plusieurs lignes, le calque doit couper exactement comme le champ : même police, pas de gras ni de marge.
  const overlayClass = multiline
    ? 'border border-transparent px-2 py-1 text-sm leading-5 whitespace-pre-wrap break-words'
    : 'px-3 py-1.5 text-sm whitespace-pre';
  const fieldProps = {
    ref: inputRef,
    value: displayDraft,
    onChange: handleChange,
    'aria-label': ariaLabel,
    onBlur: () => {
      if (ignoreBlurRef.current) return;
      setSuggestions([]);
      setActiveQuery(null);
      commit();
      if (onDone) onDone();
    },
    onKeyDown: (event) => {
      if (event.key === 'Escape') {
        setSuggestions([]);
        setActiveQuery(null);
        setRawDraft(value || '');
        if (onCancel) onCancel();
        if (onDone) onDone();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        if (suggestions.length > 0 && activeQuery) {
          applyMention(suggestions[0]);
          return;
        }
        if (multiline) return;
        commit();
        inputRef.current?.blur();
      }
    },
    disabled,
  };

  return (
    <div className="relative w-full">
      <div
        ref={overlayRef}
        className={`absolute inset-0 pointer-events-none overflow-hidden ${overlayClass} ${
          disabled ? 'text-slate-500' : 'text-slate-700'
        }`}
      >
        {displayDraft ? (
          segments.map((segment, index) => (segment.type === 'mention' ? (
            <span
              key={`seg-${index}`}
              className={`${multiline ? '' : 'font-semibold px-0.5 '}rounded ${mentionTextStyles[segment.mentionType] || 'bg-slate-100'}`}
              style={(getMentionStyle && getMentionStyle(segment.mentionType, segment.id)) || undefined}
            >
              {segment.display}
            </span>
          ) : (
            <span key={`seg-${index}`}>{segment.display}</span>
          )))
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
      </div>
      {multiline ? (
        <textarea
          {...fieldProps}
          rows={1}
          className={`block w-full resize-none overflow-hidden border border-transparent bg-transparent px-2 py-1 text-sm leading-5 text-transparent caret-slate-800 outline-none ${
            disabled ? 'cursor-not-allowed' : ''
          } ${className}`}
        />
      ) : (
        <input
          {...fieldProps}
          onScroll={(event) => {
            if (overlayRef.current) overlayRef.current.scrollLeft = event.target.scrollLeft;
          }}
          className={`w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-50 bg-transparent text-transparent caret-slate-800 ${
            disabled ? 'bg-slate-50 cursor-not-allowed' : ''
          } ${className}`}
        />
      )}
      {suggestions.length > 0 && !disabled && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
          {suggestions.map((item) => (
            <button
              key={`${item.type}-${item.id}`}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                ignoreBlurRef.current = true;
                applyMention(item);
                setTimeout(() => {
                  ignoreBlurRef.current = false;
                }, 0);
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
            >
              <span className={`px-2 py-0.5 rounded-full text-[10px] border ${mentionChipStyles[item.type] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                {MENTION_TYPE_LABELS[item.type] || item.type}
              </span>
              <span className="text-slate-700 truncate">{item.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Texte avec mentions cliquables (projet → fiche projet, cercle/rôle → sélection). `limit` : au-delà de ce nombre de
 * mentions, la suite est remplacée par un bouton « +N » (onShowAll).
 */
export const MentionText = ({
  text, empty = null, circleById, projectById, onOpenProject, onSelectCircle, getMentionStyle, limit = null, onShowAll,
}) => {
  const { ruleById, onOpenRule } = useContext(OrgMentionsContext);
  if (!text) return empty;
  const parts = parseMentions(text);
  const mentionCount = parts.filter((part) => part.type === 'mention').length;
  let shown = parts;
  let hidden = 0;
  if (limit !== null && mentionCount > limit) {
    let seen = 0;
    const cut = parts.findIndex((part) => part.type === 'mention' && (seen += 1) > limit);
    shown = parts.slice(0, cut);
    hidden = mentionCount - limit;
  }
  const rendered = shown.map((part, index) => {
    if (part.type === 'text') return <span key={`t-${index}`}>{part.value}</span>;
    if (part.mentionType === 'rule') {
      const rule = ruleById?.get(part.id);
      return (
        <button
          type="button"
          key={`m-${index}`}
          onClick={() => onOpenRule && onOpenRule(part.id)}
          disabled={!onOpenRule}
          title={rule ? `${rule.name} : ${rule.description || 'définition à écrire'}` : 'Règle de l’organisation'}
          className={`inline-flex items-center px-1.5 py-0 mx-0.5 rounded-full border text-xs font-medium align-baseline ${
            mentionChipStyles.rule} ${onOpenRule ? 'hover:opacity-80' : 'cursor-default'}`}
        >
          §&nbsp;{rule?.name || part.label}
        </button>
      );
    }
    const isProject = part.mentionType === 'project';
    const target = isProject ? projectById?.get(part.id) : circleById?.get(part.id);
    const label = target?.name || part.label;
    const clickable = isProject ? Boolean(onOpenProject) : Boolean(onSelectCircle && target);
    return (
      <button
        type="button"
        key={`m-${index}`}
        onClick={() => {
          if (isProject) onOpenProject && onOpenProject(part.id);
          else if (target && onSelectCircle) onSelectCircle(target);
        }}
        disabled={!clickable}
        className={`inline-flex items-center px-1.5 py-0 mx-0.5 rounded-full border text-xs font-medium align-baseline ${
          mentionChipStyles[part.mentionType] || 'bg-slate-100 text-slate-600 border-slate-200'
        } ${clickable ? 'hover:opacity-80' : 'cursor-default'}`}
        style={(getMentionStyle && getMentionStyle(part.mentionType, part.id)) || undefined}
      >
        @{label}
      </button>
    );
  });
  if (!hidden) return rendered;
  return [
    ...rendered,
    <button
      type="button"
      key="more"
      onClick={onShowAll}
      className="inline-flex items-center px-1.5 py-0 mx-0.5 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-500 hover:text-indigo-700 align-baseline"
    >
      +{hidden}
    </button>,
  ];
};

/**
 * Focus d'une personne dans un rôle : le lien technique entre le rôle et ses projets (c'est sur la fiche projet qu'il
 * se lit vraiment). Ici, discret : « 5 projets en cours » à droite de la personne, la liste en clair quand on la
 * déplie (les projets terminés et les propositions seulement à la demande), un crayon pour modifier.
 */
export const FocusField = ({ value, canEdit, onSave, mentionItems, getMentionStyle, mentionProps, placeholder, lead = null }) => {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const parts = parseMentions(value);
  const projectById = mentionProps?.projectById;
  const projects = parts.filter((part) => part.type === 'mention' && part.mentionType === 'project');
  const currentProjects = projects.filter((part) => isInProgress(projectById?.get(part.id)));
  const oldProjects = projects.filter((part) => !isInProgress(projectById?.get(part.id)));
  const others = parts.filter((part) => part.type === 'mention' && part.mentionType !== 'project');
  const note = parts.filter((part) => part.type === 'text').map((part) => part.value).join(' ')
    .replace(/[\s,;·]+/g, ' ').trim();
  const summary = currentProjects.length
    ? `${currentProjects.length} projet${currentProjects.length > 1 ? 's' : ''} en cours`
    : projects.length ? 'Aucun projet en cours'
      : others.length ? `${others.length} mention${others.length > 1 ? 's' : ''}` : note || 'Pas de focus';
  const hasDetail = projects.length > 0 || others.length > 0;
  const listed = [...currentProjects, ...others, ...(showOld ? oldProjects : [])];

  return (
    <div className="min-w-0">
      <div className="group flex items-center gap-2">
        {lead}
        <span className="ml-auto flex shrink-0 items-center gap-0.5">
          {hasDetail ? (
            <button type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}
              className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] text-slate-400 hover:text-slate-700"
              title={expanded ? 'Replier' : 'Voir sur quoi porte son rôle'}>
              {summary}
              <ChevronDown size={11} className={`transition ${expanded ? 'rotate-180' : ''}`} />
            </button>
          ) : (
            <span className="max-w-[12rem] truncate px-1 text-[11px] text-slate-400" title={note || undefined}>{summary}</span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => { setEditing(true); setExpanded(true); }}
              className="rounded p-1 text-slate-300 opacity-0 transition hover:bg-slate-100 hover:text-slate-600 focus:opacity-100 group-hover:opacity-100"
              title="Modifier le focus"
              aria-label="Modifier le focus"
            >
              <Pencil size={11} />
            </button>
          )}
        </span>
      </div>
      {editing ? (
        <div className="mt-1.5">
          <MentionInput
            value={String(value || '').replace(/\s*\n\s*/g, ' ')}
            autoFocus
            onCommit={(next) => onSave(next.trim())}
            onDone={() => setEditing(false)}
            mentionItems={mentionItems}
            getMentionStyle={getMentionStyle}
            placeholder={placeholder}
          />
        </div>
      ) : expanded && hasDetail && (
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          {listed.map((part, index) => {
            const project = part.mentionType === 'project' ? projectById?.get(part.id) : null;
            const name = project?.name || part.label;
            const open = part.mentionType === 'project' ? mentionProps?.onOpenProject : null;
            const old = part.mentionType === 'project' && !isInProgress(project);
            const status = old ? projectStatusLabel(project) : '';
            return (
              <React.Fragment key={`${part.mentionType}-${part.id}-${index}`}>
                {index > 0 && <span className="text-slate-300"> · </span>}
                {open ? (
                  <button type="button" onClick={() => open(part.id)}
                    className={`hover:text-indigo-700 hover:underline ${old ? 'text-slate-400' : ''}`}>
                    {name}{status && <span className="text-[10px]"> ({status})</span>}
                  </button>
                ) : (
                  <span className={old ? 'text-slate-400' : ''}>{name}{status && <span className="text-[10px]"> ({status})</span>}</span>
                )}
              </React.Fragment>
            );
          })}
          {oldProjects.length > 0 && (
            <button type="button" onClick={() => setShowOld((v) => !v)}
              className="ml-1.5 text-[11px] font-semibold text-slate-400 hover:text-indigo-700">
              {showOld ? 'masquer' : oldLabel(oldProjects.map((part) => projectById?.get(part.id)))}
            </button>
          )}
          {note && <span className="text-slate-400"> — {note}</span>}
        </p>
      )}
    </div>
  );
};
